import axios, { CancelToken } from 'axios';
import { sortBy } from 'lodash';

interface IParams {
  clinicId: string;

  /** Start of the month window being searched, `YYYY-MM-DD`. */
  date: string;
  serviceId: string;
  code?: string;
  serviceSlugName?: string;
  dentistId?: string;
  insuranceId?: string;
  familyBookingSessionId?: string;
  cancelToken?: CancelToken;
}

interface INextAvailabilityResponse {
  doctors: {
    doctorId: string;
    nextAvailabilityDate?: string | null;
  }[];
  searchingDateRanges?: string[];
}

export interface INextAvailabilityResult {
  /** `null` when no practitioner in the clinic has availability in the window. */
  nextAvailabilityDate: string | null;
  searchingDateRanges: string[];
}

/**
 * The clinic's earliest availability at or after `date`.
 *
 * The backend answers per practitioner; the earliest of those is the only one a
 * caller ever wants, so the reduction lives here rather than at every call site.
 */
export const getClinicNextAvailabilityAPI = async ({
  clinicId,
  date,
  serviceId,
  code,
  serviceSlugName,
  dentistId,
  insuranceId,
  familyBookingSessionId,
  cancelToken,
}: IParams): Promise<INextAvailabilityResult> => {
  const response = await axios.post<INextAvailabilityResponse>(
    `/clinics/${clinicId}/next-availability`,
    {
      date,
      serviceId,
      ...(code && { code }),
      ...(serviceSlugName && { serviceSlugName }),
      ...(dentistId && { dentistId }),
      ...(insuranceId && { insuranceId }),
      ...(familyBookingSessionId && { familyBookingSessionId }),
    },
    {
      baseURL: process.env.REACT_APP_AXIOS_BE_URL,
      cancelToken,
    }
  );

  const doctorsWithAvailability = (response.data?.doctors || []).filter(
    (doctor) => !!doctor.nextAvailabilityDate
  );

  const earliestDoctor = sortBy(
    doctorsWithAvailability,
    (doctor) => doctor.nextAvailabilityDate
  )[0];

  return {
    nextAvailabilityDate: earliestDoctor?.nextAvailabilityDate || null,
    searchingDateRanges: response.data?.searchingDateRanges || [],
  };
};
